import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from "react-query";

import { HttpError, HttpStatusCode, NotFoundError } from "@bps/http-client";
import { useGateways } from "@libs/api/gateways-context";
import {
  AddDeploymentRingFieldDeviceRequest,
  BulkActionResponse,
  BulkAssignDeploymentRingRequest,
  BulkAssignTenantDeploymentRingRequest,
  BulkAuthActionRequest,
  BulkCommandDispatchRequest,
  CommandResponse,
  CustomRJSFSchema,
  DeploymentDetail,
  DeploymentRing,
  DeploymentRingCreateRequest,
  DeploymentRingFieldDevice,
  DeploymentRingFieldDeviceDetail,
  DeploymentRingUpdateRequest,
  DeviceAdvancedSearchArgs,
  DeviceCommand,
  DeviceConfigurationDiff,
  DeviceDetail,
  DeviceSummary,
  FieldDevice,
  FieldDeviceAuthAction,
  FieldDeviceAuthActionRequest,
  FieldDeviceCacheOption,
  FieldDeviceClaim,
  FieldDeviceConfiguration,
  FieldDeviceDetails,
  FieldDeviceFilterArgs,
  FieldDeviceIdentifier,
  FieldDeviceProperty,
  FieldDeviceReportedConfig,
  FieldDeviceSetting,
  Rollout,
  RolloutCreateRequest,
  Selector,
  SelectorArgs,
  SelectorCreateRequest,
  SoftwarePackage,
  SoftwarePackageArgs,
  SoftwarePackageDefault,
  SoftwarePackageDefaultRequest,
  SoftwarePackageLog,
  SoftwarePackageLogArgs,
  SoftwarePackageVersion,
  SoftwarePackageVersionArgs,
  SoftwarePackageVersionConfig,
  SoftwarePublisher,
  TenantDeploymentRing,
  TenantDeploymentRingGetArgs,
  TenantDeploymentRingRequest,
  TenantDeploymentRingSummary
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { RefDataDto } from "@libs/api/types/common-dtos";
import { guid } from "@libs/common/guid";
import { PagingResponse } from "@libs/paging/paging-response.type";
import {
  cacheDeleteSingleItemInArray,
  cacheUpsertSingleItemInArray
} from "@libs/react-query/react-query-cache.utils";

import { useFeedbackContext } from "../../../stores/feedback-context/feedback-context";
import { base64EncodeRolloutRequest } from "../plt/plt-gateway.dtos";

export const FieldOpsCacheKeys = {
  FieldDevice: "field-device",
  FieldDeviceDetails: "field-device-details",
  FieldDevices: "field-devices",
  FieldServices: "field-services",
  DeploymentRing: "deployment-ring",
  DeploymentRings: "deployment-rings",
  Rollouts: "rollouts",
  Rollout: "rollout",
  RingMembers: "ring-members",
  FieldDeviceIdentifiers: "field-device-identifiers",
  FieldDeviceIdentifierHistory: "field-device-identifier-history",
  FieldDeviceProperties: "field-device-properties",
  FieldDeviceClaims: "field-device-claims",
  SoftwarePackages: "software-packages",
  SoftwarePublishers: "software-publishers",
  SoftwarePackageVersion: "software-package-version",
  SoftwarePackageVersions: "software-package-versions",
  SoftwarePackageVersionsRollout: "software-package-versions-rollout",
  SoftwarePackageVersionConfig: "software-package-version-config",
  SoftwarePackageDefaults: "software-package-defaults",
  FieldDeviceAuthActions: "field-device-auth-actions",
  AuthActionsRefData: "auth-actions-ref-data",
  DeploymentRingFieldDeviceDetails: "deployment-ring-field-device-details",
  DeploymentRingFieldDevice: "deployment-ring-field-device",
  FieldDeviceReportedConfig: "field-device-reported-config",
  FieldDeviceDesiredConfig: "field-device-desired-config",
  SoftwarePackageVersionCommandJson: "software-package-version-command-json",
  DeviceConfigurationDiff: "device-configuration-diff",
  Selectors: "selectors",
  Selector: "selector",
  TenantFieldDevices: "tenant-field-devices",
  TenantDeploymentRings: "tenant-selectors",
  SelectorEvaluation: "selector-evaluation",
  SelectorDataTypeRefData: "selector-datatype-ref-data",
  DeviceLogs: "onsite-device-logs",
  FieldDeviceSettings: "field-device-settings",
  FieldDeviceSettingsRefData: "field-device-settings-ref-data",
  DeploymentDetail: "deployment-detail",
  AdvancedSearchResults: "advanced-search-results"
};

// ----- Field Devices ---------------------------------------------------------------

export const useFieldDevices = (filterArgs?: FieldDeviceFilterArgs) => {
  const { fieldOpsGateway } = useGateways();

  return useInfiniteQuery<PagingResponse<FieldDeviceDetails>, HttpError>(
    [FieldOpsCacheKeys.FieldDevices, filterArgs],
    async page => {
      return await fieldOpsGateway.getDevices(filterArgs, page.pageParam);
    },
    {
      getNextPageParam: lastPage => lastPage.next
    }
  );
};

export const useUpdateFieldDevice = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<FieldDevice, HttpError, FieldDevice>(
    request => fieldOpsGateway.updateFieldDevice(request),
    {
      onSuccess: async fieldDevice => {
        queryClient.setQueryData(
          [FieldOpsCacheKeys.FieldDevice, fieldDevice.id],
          fieldDevice
        );

        await queryClient.refetchQueries([FieldOpsCacheKeys.FieldDevices]);

        success("A field device has been updated.");
      },
      onError: async e => {
        error(e.message);
      }
    }
  );
};

export const useDeleteFieldDevice = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<void, HttpError, guid>(
    fieldDeviceId => fieldOpsGateway.deleteFieldDevice(fieldDeviceId),
    {
      onSuccess: async (_, fieldDeviceId) => {
        queryClient.removeQueries([
          FieldOpsCacheKeys.FieldDevice,
          fieldDeviceId
        ]);
        queryClient.removeQueries([
          FieldOpsCacheKeys.FieldDeviceDetails,
          fieldDeviceId
        ]);

        await queryClient.refetchQueries([FieldOpsCacheKeys.FieldDevices]);

        success("A field device has been deleted.");
      },
      onError: async e => {
        error(e.message);
      }
    }
  );
};

export const useFieldDeviceQuery = (
  id: string,
  options?: Omit<UseQueryOptions<FieldDevice, Error>, "queryKey" | "queryFn">
) => {
  const { fieldOpsGateway } = useGateways();
  const queryClient = useQueryClient();

  return useQuery<FieldDevice, HttpError>(
    [FieldOpsCacheKeys.FieldDevice, id],
    () => fieldOpsGateway.getDevice(id),
    {
      ...options,
      initialData: () => {
        const cache = queryClient.getQueryData<
          InfiniteData<PagingResponse<FieldDevice[]>>
        >([FieldOpsCacheKeys.FieldDevice, null]);

        const devicesFlattened = cache?.pages.reduce(
          (acc: FieldDevice[], curVal) => {
            return curVal.results !== null && curVal.results !== undefined
              ? acc.concat(...curVal.results)
              : [];
          },
          []
        );

        return devicesFlattened?.find(d => d.id === id);
      }
    }
  );
};

export const useFieldDevicesDetails = (fieldDeviceId: string) => {
  const { fieldOpsGateway } = useGateways();
  const queryClient = useQueryClient();

  return useQuery<FieldDeviceDetails, HttpError>(
    [FieldOpsCacheKeys.FieldDeviceDetails, fieldDeviceId],
    async () => {
      return await fieldOpsGateway.getDeviceDetails(fieldDeviceId);
    },
    {
      initialData: () => {
        // Don't send request if we already have the data in previous cache of data collection, instead initialise from that data.
        const cache = queryClient.getQueryData<FieldDeviceDetails[]>([
          FieldOpsCacheKeys.FieldDevices
        ]);
        return cache?.find(d => d.id === fieldDeviceId);
      }
    }
  );
};

// ----- Field Device Identifiers --------------------------------------------

export const useFieldDeviceIdentifiers = (fieldDeviceId: string) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<FieldDeviceIdentifier[], HttpError>(
    [FieldOpsCacheKeys.FieldDeviceIdentifiers, fieldDeviceId],
    async () => {
      return await fieldOpsGateway.getDeviceIdentifiers(fieldDeviceId);
    }
  );
};

export const useMostRecentFieldDeviceIdentifiers = (fieldDeviceId: string) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<FieldDeviceIdentifier[], HttpError>(
    [FieldOpsCacheKeys.FieldDeviceIdentifiers, fieldDeviceId],
    async () => {
      return await fieldOpsGateway.getMostRecentDeviceIdentifierHistory(
        fieldDeviceId
      );
    }
  );
};

export const useFieldDeviceIdentifierHistory = (
  fieldDeviceId: string,
  identifierType: string
) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<FieldDeviceIdentifier[], HttpError>(
    [
      FieldOpsCacheKeys.FieldDeviceIdentifierHistory,
      fieldDeviceId,
      identifierType
    ],
    async () => {
      return await fieldOpsGateway.getDeviceIdentifierHistory(
        fieldDeviceId,
        identifierType
      );
    }
  );
};

// ----- Field Device Properties --------------------------------------------

export const useFieldDeviceProperties = (fieldDeviceId: string) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<FieldDeviceProperty[], HttpError>(
    [FieldOpsCacheKeys.FieldDeviceProperties, fieldDeviceId],
    async () => {
      return await fieldOpsGateway.getDeviceProperties(fieldDeviceId);
    }
  );
};

// ----- Field Device Claims --------------------------------------------

export const useFieldDeviceClaims = (fieldDeviceId: string) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<FieldDeviceClaim[], HttpError>(
    [FieldOpsCacheKeys.FieldDeviceClaims, fieldDeviceId],
    async () => {
      return await fieldOpsGateway.getDeviceClaims(fieldDeviceId);
    }
  );
};

// ----- Field Device Settings --------------------------------------------

export const useFieldDeviceSettings = (fieldDeviceId: string) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<FieldDeviceSetting[], HttpError>(
    [FieldOpsCacheKeys.FieldDeviceSettings, fieldDeviceId],
    async () => {
      return await fieldOpsGateway.getFieldDeviceSettings(fieldDeviceId);
    }
  );
};

export const useCreateFieldDeviceSetting = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();
  return useMutation<FieldDeviceSetting, HttpError, FieldDeviceSetting>(
    request => fieldOpsGateway.postFieldDeviceSetting(request),
    {
      onSuccess: async fieldDeviceSetting => {
        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [
            FieldOpsCacheKeys.FieldDeviceSettings,
            fieldDeviceSetting.fieldDeviceId
          ],
          item: fieldDeviceSetting
        });

        success("A field device setting has been created.");
      },
      onError: async e => {
        error(e.message);
      }
    }
  );
};

export const useUpdateFieldDeviceSetting = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();
  return useMutation<FieldDeviceSetting, HttpError, FieldDeviceSetting>(
    request => fieldOpsGateway.updateFieldDeviceSetting(request),
    {
      onSuccess: async fieldDeviceSetting => {
        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [
            FieldOpsCacheKeys.FieldDeviceSettings,
            fieldDeviceSetting.fieldDeviceId
          ],
          item: fieldDeviceSetting
        });

        success("A field device setting has been updated.");
      },
      onError: async e => {
        error(e.message);
      }
    }
  );
};

export const useFieldDeviceSettingsRefData = () => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<RefDataDto[], HttpError>(
    [FieldOpsCacheKeys.FieldDeviceSettingsRefData],
    async () => {
      return await fieldOpsGateway.getFieldDeviceSettingTypeRefData();
    }
  );
};

// ----- Deployment Detail ---------------------------------------------------

export const useDeploymentDetail = (fieldDeviceId: string) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<DeploymentDetail>(
    [FieldOpsCacheKeys.DeploymentDetail, fieldDeviceId],
    async () => {
      return await fieldOpsGateway.getDeploymentDetail(fieldDeviceId);
    }
  );
};

// ----- Device Advanced Search ---------------------------------------------------

export const useDeviceAdvancedSearch = (
  filterArgs: DeviceAdvancedSearchArgs
) => {
  const { fieldOpsGateway } = useGateways();
  return useInfiniteQuery<PagingResponse<DeviceDetail>, HttpError>(
    [FieldOpsCacheKeys.AdvancedSearchResults, filterArgs],
    async page => {
      return await fieldOpsGateway.postDeviceAdvancedSearch(
        filterArgs,
        page.pageParam
      );
    },
    {
      getNextPageParam: lastPage => lastPage.next
    }
  );
};

// ----- Rings ---------------------------------------------------------------

export const useDeploymentRings = () => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<DeploymentRing[]>(
    [FieldOpsCacheKeys.DeploymentRings],
    async () => {
      return await fieldOpsGateway.getDeploymentRings();
    }
  );
};

export const useDeploymentRing = (deploymentRingId: string) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<DeploymentRing>(
    [FieldOpsCacheKeys.DeploymentRing, deploymentRingId],
    async () => {
      return await fieldOpsGateway.getDeploymentRing(deploymentRingId);
    }
  );
};

export const useCreateDeploymentRing = () => {
  const { fieldOpsGateway } = useGateways();
  const { success, error } = useFeedbackContext();
  const queryClient = useQueryClient();
  return useMutation<DeploymentRing, HttpError, DeploymentRingCreateRequest>(
    request => fieldOpsGateway.postDeploymentRing(request),
    {
      onSuccess: async deploymentRing => {
        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [FieldOpsCacheKeys.DeploymentRings],
          item: deploymentRing
        });

        success("A deployment ring has been created.");
      },
      onError: async (e, request) => {
        if (e.httpStatusCode === HttpStatusCode.Conflict) {
          error(`A deployment ring already exists with code: ${request.code}`);
        } else {
          error(e.message);
        }
      }
    }
  );
};

export const useUpdateDeploymentRing = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();
  return useMutation<DeploymentRing, Error, DeploymentRingUpdateRequest>(
    request => fieldOpsGateway.updateDeploymentRings(request),
    {
      onSuccess: async deploymentRing => {
        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [FieldOpsCacheKeys.DeploymentRings],
          item: deploymentRing
        });

        success("A deployment ring has been updated.");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

export const useDeleteDeploymentRing = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();
  return useMutation<void, Error, guid>(
    request => fieldOpsGateway.deleteDeploymentRings(request),
    {
      onSuccess: async (_, request) => {
        cacheDeleteSingleItemInArray({
          queryClient,
          id: request,
          queryKey: [FieldOpsCacheKeys.DeploymentRings]
        });

        success("A deployment ring has been deleted.");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

// ----- Rollouts ---------------------------------------------------------------
export const useRollouts = (deploymentRingId?: string) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<Rollout[]>(
    [FieldOpsCacheKeys.Rollouts, deploymentRingId],
    async () => {
      return await fieldOpsGateway.getRollouts(deploymentRingId);
    }
  );
};

export const useRollout = (rolloutId?: string) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<Rollout>([FieldOpsCacheKeys.Rollout, rolloutId], async () => {
    return await fieldOpsGateway.getRollout(rolloutId);
  });
};

export const useCreateRollout = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();
  return useMutation<Rollout, Error, RolloutCreateRequest>(
    request => fieldOpsGateway.postRollout(base64EncodeRolloutRequest(request)),
    {
      onSuccess: async rollout => {
        await queryClient.refetchQueries([
          FieldOpsCacheKeys.SoftwarePackageVersionsRollout,
          rollout.id
        ]);

        queryClient.setQueryData(
          [FieldOpsCacheKeys.Rollout, rollout.id],
          rollout
        );

        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [FieldOpsCacheKeys.Rollouts, rollout.deploymentRingId],
          item: rollout,
          asFirstItem: true
        });

        success("A rollout has been created.");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

export const useUpdateRollout = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();
  return useMutation<Rollout, Error, RolloutCreateRequest>(
    request =>
      fieldOpsGateway.updateRollout(base64EncodeRolloutRequest(request)),
    {
      onSuccess: async rollout => {
        await queryClient.refetchQueries([
          FieldOpsCacheKeys.SoftwarePackageVersionsRollout,
          rollout.id
        ]);

        queryClient.setQueryData(
          [FieldOpsCacheKeys.Rollout, rollout.id],
          rollout
        );

        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [FieldOpsCacheKeys.Rollouts, rollout.deploymentRingId],
          item: rollout
        });

        success("A rollout has been updated.");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

export const useDeleteRollout = (deploymentRingId: string) => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();
  return useMutation<void, Error, guid>(
    request => fieldOpsGateway.deleteRollout(request),
    {
      onSuccess: async (_, request) => {
        queryClient.removeQueries([FieldOpsCacheKeys.Rollout, request]);

        cacheDeleteSingleItemInArray({
          queryClient,
          id: request,
          queryKey: [FieldOpsCacheKeys.Rollouts, deploymentRingId]
        });

        success("A rollout has been deleted.");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

// ----- Software Packages ------------------------------------------------------------

export const useSoftwarePackages = (args?: SoftwarePackageArgs) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<SoftwarePackage[], HttpError>(
    [FieldOpsCacheKeys.SoftwarePackages, args],
    async () => {
      return await fieldOpsGateway.getSoftwarePackages(args);
    }
  );
};

// ----- Software Publishers ------------------------------------------------------------

export const useSoftwarePublishers = () => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<SoftwarePublisher[], HttpError>(
    [FieldOpsCacheKeys.SoftwarePublishers],
    async () => {
      return await fieldOpsGateway.getSoftwarePublishers();
    }
  );
};

// ----- Software Package Versions ----------------------------------------------------

export const useSoftwarePackageVersion = (softwarePackageVersionId: string) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<SoftwarePackageVersion, HttpError>(
    [FieldOpsCacheKeys.SoftwarePackageVersion, softwarePackageVersionId],
    async () => {
      return await fieldOpsGateway.getSoftwarePackageVersion(
        softwarePackageVersionId
      );
    }
  );
};

export const useSoftwarePackageVersions = (
  args?: SoftwarePackageVersionArgs
) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<SoftwarePackageVersion[], HttpError>(
    [FieldOpsCacheKeys.SoftwarePackageVersions, args],
    async () => {
      return await fieldOpsGateway.getSoftwarePackageVersions(args);
    }
  );
};

export const useSoftwarePackageVersionsForRollout = (rolloutId: string) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<SoftwarePackageVersion[], HttpError>(
    [FieldOpsCacheKeys.SoftwarePackageVersionsRollout, rolloutId],
    async () => {
      return await fieldOpsGateway.getSoftwarePackageVersionsForRollout(
        rolloutId
      );
    }
  );
};

// ----- Software Package Version Config ------------------------------------------------------------

export const useSoftwarePackageVersionConfig = (
  softwarePackageVersionId: string
) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<SoftwarePackageVersionConfig[], HttpError>(
    [FieldOpsCacheKeys.SoftwarePackageVersionConfig, softwarePackageVersionId],
    async () => {
      return await fieldOpsGateway.getSoftwarePackageVersionConfig(
        softwarePackageVersionId
      );
    }
  );
};

// ----- Software Package Defaults ----------------------------------------------------

export const useSoftwarePackageDefaults = () => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<SoftwarePackageDefault[], HttpError>(
    [FieldOpsCacheKeys.SoftwarePackageDefaults],
    async () => {
      return await fieldOpsGateway.getSoftwarePackageDefaults();
    }
  );
};

export const useUpdateSoftwarePackageDefault = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();
  return useMutation<SoftwarePackageDefault, Error, SoftwarePackageDefault>(
    request => fieldOpsGateway.updateSoftwarePackageDefault(request),
    {
      onSuccess: async softwarePackageDefault => {
        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [FieldOpsCacheKeys.SoftwarePackageDefaults],
          item: softwarePackageDefault
        });

        success("Updated software package default version.");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

export const useCreateSoftwarePackageDefault = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();
  return useMutation<
    SoftwarePackageDefault,
    Error,
    SoftwarePackageDefaultRequest
  >(request => fieldOpsGateway.postSoftwarePackageDefault(request), {
    onSuccess: async softwarePackageDefault => {
      cacheUpsertSingleItemInArray({
        queryClient,
        queryKey: [FieldOpsCacheKeys.SoftwarePackageDefaults],
        item: softwarePackageDefault
      });

      success("Created software package default version.");
    },
    onError: e => {
      error(e.message);
    }
  });
};

export const useRegenerateDefaultFieldConfig = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  return async () => {
    try {
      await fieldOpsGateway.regenerateSoftwarePackageDefaultConfig();
      success("Regenerated default field configuration.");
    } catch {
      error("Error regenerating default field configuration.");
    }
  };
};

export const usePurgeFieldmanCDN = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  return async () => {
    try {
      await fieldOpsGateway.purgeFieldmanCDN();
      success("Successfully sent request to purge Fieldman CDN.");
    } catch {
      error("Error requesting to purge Fieldman CDN.");
    }
  };
};

// ----- Auth Actions ------------------------------------------------------------
export const useFieldDeviceAuthActions = (
  fieldDeviceId: string,
  showExecuted: boolean
) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<FieldDeviceAuthAction[] | undefined>(
    [FieldOpsCacheKeys.FieldDeviceAuthActions, fieldDeviceId, showExecuted],
    async () => {
      return await fieldOpsGateway.getFieldDeviceAuthActions(
        fieldDeviceId,
        showExecuted
      );
    }
  );
};

export const useCreateFieldDeviceAuthActions = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<
    FieldDeviceAuthAction,
    Error,
    FieldDeviceAuthActionRequest
  >(request => fieldOpsGateway.postFieldDeviceAuthActions(request), {
    onSuccess: async fieldDeviceAuthAction => {
      cacheUpsertSingleItemInArray({
        queryClient,
        queryKey: [
          FieldOpsCacheKeys.FieldDeviceAuthActions,
          fieldDeviceAuthAction.fieldDeviceId,
          false
        ],
        item: fieldDeviceAuthAction
      });

      cacheUpsertSingleItemInArray({
        queryClient,
        queryKey: [
          FieldOpsCacheKeys.FieldDeviceAuthActions,
          fieldDeviceAuthAction.fieldDeviceId,
          true
        ],
        item: fieldDeviceAuthAction
      });

      success("An auth action has been created.");
    },
    onError: e => {
      error(e.message);
    }
  });
};

export const useUpdateFieldDeviceAuthActions = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<FieldDeviceAuthAction, Error, FieldDeviceAuthAction>(
    request => fieldOpsGateway.updateFieldDeviceAuthActions(request),
    {
      onSuccess: async fieldDeviceAuthAction => {
        if (!!fieldDeviceAuthAction.runDate) {
          cacheUpsertSingleItemInArray({
            queryClient,
            queryKey: [
              FieldOpsCacheKeys.FieldDeviceAuthActions,
              fieldDeviceAuthAction.fieldDeviceId,
              false
            ],
            item: fieldDeviceAuthAction
          });
        }

        cacheUpsertSingleItemInArray({
          queryClient,
          queryKey: [
            FieldOpsCacheKeys.FieldDeviceAuthActions,
            fieldDeviceAuthAction.fieldDeviceId,
            true
          ],
          item: fieldDeviceAuthAction
        });

        success("An auth action has been updated.");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

export const useDeleteFieldDeviceAuthActions = (fieldDeviceId: string) => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>(
    request => fieldOpsGateway.deleteFieldDeviceAuthActions(request),
    {
      onSuccess: async (_, args) => {
        // Remove record from both caches (executed/ not executed) for deleted auth action.
        cacheDeleteSingleItemInArray({
          queryClient,
          id: args,
          queryKey: [
            FieldOpsCacheKeys.FieldDeviceAuthActions,
            fieldDeviceId,
            true
          ]
        });
        cacheDeleteSingleItemInArray({
          queryClient,
          id: args,
          queryKey: [
            FieldOpsCacheKeys.FieldDeviceAuthActions,
            fieldDeviceId,
            false
          ]
        });

        success("An auth action has been deleted.");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

export const useAuthActionsRefData = () => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<RefDataDto[]>(
    [FieldOpsCacheKeys.AuthActionsRefData],
    async () => {
      return await fieldOpsGateway.getAuthActionRefData();
    }
  );
};

// ----------- Field Device Reported Configuration -----------------------------

export const useFieldDeviceReportedConfig = (fieldDeviceId: string) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<FieldDeviceReportedConfig[], HttpError>(
    [FieldOpsCacheKeys.FieldDeviceReportedConfig, fieldDeviceId],
    async () => {
      return await fieldOpsGateway.getFieldDeviceReportedConfig(fieldDeviceId);
    }
  );
};

// ----- Field Device Deployment Rings ---------------------------------------------------------------

export const useFieldDeviceDeploymentRings = (
  fieldDeviceId?: string,
  deploymentRingId?: string
) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<DeploymentRingFieldDevice[], HttpError>(
    [
      FieldOpsCacheKeys.DeploymentRingFieldDevice,
      fieldDeviceId,
      deploymentRingId
    ],
    async () => {
      return await fieldOpsGateway.getFieldDeviceDeploymentRings(
        fieldDeviceId,
        deploymentRingId
      );
    }
  );
};

export const useFieldDeviceDeploymentRingDetail = (
  deploymentRingFieldDeviceId: string
) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<DeploymentRingFieldDeviceDetail, HttpError>(
    [
      FieldOpsCacheKeys.DeploymentRingFieldDeviceDetails,
      deploymentRingFieldDeviceId
    ],
    async () => {
      return await fieldOpsGateway.getFieldDeviceDeploymentRingDetail(
        deploymentRingFieldDeviceId
      );
    }
  );
};

export const useAddDeviceDeploymentRing = () => {
  const { fieldOpsGateway } = useGateways();
  const queryClient = useQueryClient();
  const { error } = useFeedbackContext();
  return useMutation<
    DeploymentRingFieldDevice,
    Error,
    AddDeploymentRingFieldDeviceRequest
  >(request => fieldOpsGateway.postFieldDeviceDeploymentRing(request), {
    onSuccess: async (_, request) => {
      await queryClient.refetchQueries([
        FieldOpsCacheKeys.DeploymentRingFieldDevice,
        request.fieldDeviceId,
        undefined
      ]);
    },
    onError: e => {
      error(e.message);
    }
  });
};

export const useUpdateFieldDeviceDeploymentRing = (fieldDeviceId: string) => {
  const { fieldOpsGateway } = useGateways();
  const queryClient = useQueryClient();
  return useMutation<
    DeploymentRingFieldDevice,
    Error,
    DeploymentRingFieldDevice
  >(request => fieldOpsGateway.updateFieldDeviceDeploymentRing(request), {
    onSuccess: async () => {
      await queryClient.refetchQueries([
        FieldOpsCacheKeys.DeploymentRingFieldDevice,
        fieldDeviceId,
        undefined
      ]);
    }
  });
};

export const useDeleteDeviceDeploymentRing = (fieldDeviceId: string) => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>(
    deploymentRingFieldDeviceId =>
      fieldOpsGateway.deleteFieldDeviceDeploymentRing(
        deploymentRingFieldDeviceId
      ),
    {
      onSuccess: async (_, id) => {
        queryClient.removeQueries([
          FieldOpsCacheKeys.DeploymentRingFieldDeviceDetails,
          id
        ]);

        await queryClient.refetchQueries([
          FieldOpsCacheKeys.DeploymentRingFieldDevice,
          fieldDeviceId,
          undefined
        ]);

        success("A deployment ring has been removed.");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

export const useClearDeploymentRingConfigurationCache = (
  deploymentRingId: string
) => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();

  return async () => {
    try {
      await fieldOpsGateway.clearDeploymentRingBlock(deploymentRingId);

      success(
        "The deployment ring cache has been cleared for this deployment ring."
      );
    } catch {
      error("An error occurred clearing the deployment ring block.");
    }
  };
};

// ----------- Field Device Configuration -----------------------------

export const useClearDeviceFieldConfigurationCache = (
  fieldDeviceId: string
) => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return async () => {
    try {
      await fieldOpsGateway.clearDeviceFieldConfigurationCache(fieldDeviceId);

      // Clear desired and dif react cache
      await queryClient.refetchQueries([
        FieldOpsCacheKeys.FieldDeviceDesiredConfig,
        fieldDeviceId
      ]);
      await queryClient.refetchQueries([
        FieldOpsCacheKeys.DeviceConfigurationDiff,
        fieldDeviceId
      ]);

      success("Field configuration cache cleared for this field device.");
    } catch {
      error("Error requesting to purge field configuration cache.");
    }
  };
};

export const useClearDeviceCaches = (fieldDeviceId: string) => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return async (cacheOption: FieldDeviceCacheOption) => {
    try {
      await fieldOpsGateway.clearDeviceCaches(fieldDeviceId, cacheOption.type);

      // Clear desired and dif react cache
      await queryClient.refetchQueries([
        FieldOpsCacheKeys.FieldDeviceDesiredConfig,
        fieldDeviceId
      ]);
      await queryClient.refetchQueries([
        FieldOpsCacheKeys.DeviceConfigurationDiff,
        fieldDeviceId
      ]);

      success(`The cache for "${cacheOption.text}" has been cleared`);
    } catch {
      error(
        `The cache for "${cacheOption.text}" has not been cleared as an error has occurred.`
      );
    }
  };
};

// ----------- Field Device Desired Configuration -----------------------------

export const useFieldDeviceConfiguration = (
  fieldDeviceId: string,
  fieldConfigurationVersion?: string
) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<FieldDeviceConfiguration, HttpError>(
    [
      FieldOpsCacheKeys.FieldDeviceDesiredConfig,
      fieldDeviceId,
      fieldConfigurationVersion
    ],
    async () => {
      return await fieldOpsGateway.getFieldDeviceDesiredConfig(
        fieldDeviceId,
        fieldConfigurationVersion
      );
    }
  );
};

// ----------- Software Package Command Json -----------------------------

export const useSoftwarePackageCommandJson = (
  softwarePackageVersionId?: string
) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<CustomRJSFSchema[], HttpError>(
    [
      FieldOpsCacheKeys.SoftwarePackageVersionCommandJson,
      softwarePackageVersionId
    ],
    async () => {
      try {
        return await fieldOpsGateway.getSoftwarePackgeCommandJson(
          softwarePackageVersionId
        );
      } catch (e) {
        if (e instanceof NotFoundError) {
          return [];
        }
        return e;
      }
    }
  );
};

// ----------- Device Configuration Diff -----------------------------

export const useDeviceConfigurationDiff = (
  fieldDeviceId: string,
  fieldConfigurationVersion?: string
) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<DeviceConfigurationDiff, HttpError>(
    [
      FieldOpsCacheKeys.DeviceConfigurationDiff,
      fieldDeviceId,
      fieldConfigurationVersion
    ],
    async () => {
      return await fieldOpsGateway.getDeviceConfigurationDiff(
        fieldDeviceId,
        fieldConfigurationVersion
      );
    }
  );
};

// ----------- Selectors -----------------------------

export const useSelectors = (args?: SelectorArgs) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<Selector[], HttpError>(
    [FieldOpsCacheKeys.Selectors, args],
    async () => {
      return await fieldOpsGateway.getSelectors(args);
    }
  );
};

export const useCreateSelector = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<Selector, HttpError, SelectorCreateRequest>(
    request => fieldOpsGateway.postSelector(request),
    {
      onSuccess: async () => {
        // Doing full refetch here because we would have to pass filter args here + upsert for every possible arg.
        await queryClient.refetchQueries([FieldOpsCacheKeys.Selectors]);

        success("A selector has been created.");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

//Delete Selector
export const useDeleteSelector = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<void, HttpError, guid>(
    id => fieldOpsGateway.deleteSelector(id),
    {
      onSuccess: async () => {
        await queryClient.refetchQueries([FieldOpsCacheKeys.Selectors]);

        success("Selector has been deleted.");
      },
      onError: e => {
        error(e.detail as string);
      }
    }
  );
};

export const useUpdateSelector = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  // Returns a Selector and takes a selector
  return useMutation<Selector, HttpError, Selector>(
    request => fieldOpsGateway.putSelector(request),
    {
      onSuccess: async response => {
        // Doing full refetch here because we would have to pass filter args here + upsert for every possible arg.
        await queryClient.refetchQueries([FieldOpsCacheKeys.Selectors]);

        // Remove cached evaluation so that it can be re-evaluated.
        await queryClient.invalidateQueries([
          FieldOpsCacheKeys.SelectorEvaluation,
          response.id
        ]);

        // Update single selector react cache with response
        queryClient.setQueryData(
          [FieldOpsCacheKeys.Selector, response.id],
          response
        );

        success("Selector has been updated.");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

export const useSelector = (selectorId: guid) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<Selector, HttpError>(
    [FieldOpsCacheKeys.Selector, selectorId],
    async () => {
      return await fieldOpsGateway.getSelector(selectorId);
    }
  );
};

export const useSelectorEvaluate = (selectorId: guid) => {
  const { fieldOpsGateway } = useGateways();
  const queryClient = useQueryClient();

  return useQuery<DeviceSummary[], HttpError>(
    [FieldOpsCacheKeys.SelectorEvaluation, selectorId],
    async () => {
      const response = await fieldOpsGateway.getSelectorEvaluate(selectorId); // Evaluate selector first

      await queryClient.refetchQueries([FieldOpsCacheKeys.Selectors]); // Then update selectors table (to update device count updated from evaluation)

      // Make sure to invalidate selector cache, if the device count changes from the evaluation... the etag for the get selector by id request will be invalid.
      await queryClient.invalidateQueries([
        FieldOpsCacheKeys.Selector,
        selectorId
      ]);
      return response;
    }
  );
};

export const useSelectorDataTypeRefData = () => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<RefDataDto[]>(
    [FieldOpsCacheKeys.SelectorDataTypeRefData],
    async () => {
      return await fieldOpsGateway.getSelectorDataTypeRefData();
    }
  );
};

// ----------- Field Gateway --------------------------------

export const useDispatchDeviceCommand = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  return useMutation<CommandResponse, HttpError, DeviceCommand>(
    command => fieldOpsGateway.dispatchDeviceCommand(command),
    {
      onSuccess: async () => {
        success("Successfully sent command to device.");
      },
      onError: async err => {
        error("Error sending command to device.");
        throw err;
      }
    }
  );
};

// ----------- Device Logs -----------------------------

export const useDeviceLogs = (args: SoftwarePackageLogArgs) => {
  const { fieldOpsGateway } = useGateways();

  return useInfiniteQuery<PagingResponse<SoftwarePackageLog>, HttpError>(
    [FieldOpsCacheKeys.DeviceLogs, args],
    async page => {
      return await fieldOpsGateway.getDeviceLogs(args, page.pageParam);
    },
    {
      getNextPageParam: lastPage => lastPage.next
    }
  );
};

// ----------- Tenant -----------------------------

export const useTenantDevices = (tenantId: guid) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<DeviceSummary[], HttpError>(
    [FieldOpsCacheKeys.TenantFieldDevices, tenantId],
    async () => {
      return await fieldOpsGateway.getTenantFieldDevices(tenantId);
    }
  );
};

// ----------- Tenant Selector -----------------------------

export const useTenantDeploymentRings = (
  args?: TenantDeploymentRingGetArgs
) => {
  const { fieldOpsGateway } = useGateways();

  return useQuery<TenantDeploymentRingSummary[], HttpError>(
    [FieldOpsCacheKeys.TenantDeploymentRings, args],
    async () => {
      return await fieldOpsGateway.getTenantDeploymentRingByArgs(args);
    }
  );
};

export const useCreateTenantDeploymentRings = () => {
  const { fieldOpsGateway } = useGateways();
  const { error } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<
    TenantDeploymentRing,
    HttpError,
    TenantDeploymentRingRequest
  >(request => fieldOpsGateway.postTenantDeploymentRing(request), {
    onSuccess: async () => {
      await queryClient.refetchQueries([
        FieldOpsCacheKeys.TenantDeploymentRings
      ]);
    },
    onError: e => {
      error(e.message);
    }
  });
};

export const useDeleteTenantDeploymentRings = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<void, HttpError, guid>(
    id => fieldOpsGateway.deleteTenantDeploymentRing(id),
    {
      onSuccess: async () => {
        await queryClient.refetchQueries([
          FieldOpsCacheKeys.TenantDeploymentRings
        ]);

        success("A deployment ring has been removed.");
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

export const useUpdateTenantDeploymentRings = () => {
  const { fieldOpsGateway } = useGateways();
  const { error } = useFeedbackContext();
  const queryClient = useQueryClient();

  return useMutation<TenantDeploymentRing, HttpError, TenantDeploymentRing>(
    TenantDeploymentRing =>
      fieldOpsGateway.updateTenantDeploymentRing(TenantDeploymentRing),
    {
      onSuccess: async () => {
        // Hard refresh as response from update does not contain deployment ring info - display name, code etc so cannot insert response in react cache.
        await queryClient.refetchQueries([
          FieldOpsCacheKeys.TenantDeploymentRings
        ]);
      },
      onError: e => {
        error(e.message);
      }
    }
  );
};

// ----------- Bulk Actions -----------------------------

export const useAssignTenantDeploymentRingBulkAction = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  return useMutation<
    BulkActionResponse,
    HttpError,
    BulkAssignTenantDeploymentRingRequest
  >(
    request =>
      fieldOpsGateway.postAssignTenantDeploymentRingBulkAction(request),
    {
      onSuccess: async () => {
        success("Successfully ran the bulk action.");
      },
      onError: async err => {
        error("Error running the bulk action.");
        throw err;
      }
    }
  );
};

export const useUnassignTenantDeploymentRingBulkAction = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  return useMutation<
    BulkActionResponse,
    HttpError,
    BulkAssignTenantDeploymentRingRequest
  >(
    request =>
      fieldOpsGateway.postUnassignTenantDeploymentRingBulkAction(request),
    {
      onSuccess: async () => {
        success("Successfully ran the bulk action.");
      },
      onError: async err => {
        error("Error running the bulk action.");
        throw err;
      }
    }
  );
};

export const useAssignDeploymentRingBulkAction = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  return useMutation<
    BulkActionResponse,
    HttpError,
    BulkAssignDeploymentRingRequest
  >(request => fieldOpsGateway.postAssignDeploymentRingBulkAction(request), {
    onSuccess: async () => {
      success("Successfully ran the bulk action.");
    },
    onError: async err => {
      error("Error running the bulk action.");
      throw err;
    }
  });
};

export const useUnassignDeploymentRingBulkAction = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  return useMutation<
    BulkActionResponse,
    HttpError,
    BulkAssignDeploymentRingRequest
  >(request => fieldOpsGateway.postUnassignDeploymentRingBulkAction(request), {
    onSuccess: async () => {
      success("Successfully ran the bulk action.");
    },
    onError: async err => {
      error("Error running the bulk action.");
      throw err;
    }
  });
};

export const useBulkAuthAction = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  return useMutation<BulkActionResponse, HttpError, BulkAuthActionRequest>(
    request => fieldOpsGateway.postBulkAuthAction(request),
    {
      onSuccess: async () => {
        success("Successfully ran the bulk action.");
      },
      onError: async err => {
        error("Error running the bulk action.");
        throw err;
      }
    }
  );
};

export const useBulkCommandDispatch = () => {
  const { fieldOpsGateway } = useGateways();
  const { error, success } = useFeedbackContext();
  return useMutation<BulkActionResponse, HttpError, BulkCommandDispatchRequest>(
    request => fieldOpsGateway.postBulkCommandDispatch(request),
    {
      onSuccess: async () => {
        success("Successfully ran the bulk action.");
      },
      onError: async err => {
        error("Error running the bulk action.");
        throw err;
      }
    }
  );
};
